import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  PhoneAndroid as MpesaIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useLoan,
  useLoanSchedule,
  useLoanTransactions,
} from '../../hooks/loans/useLoans';
import { format } from 'date-fns';
import MpesaPaymentDialog from '../../components/payments/MpesaPaymentDialog';

const LoanDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const loanId = id || '';

  const [mpesaDialogOpen, setMpesaDialogOpen] = useState(false);

  // Fetch data
  const { data: loan, isLoading: loanLoading, error: loanError } = useLoan(loanId);
  const { data: schedule, isLoading: scheduleLoading } = useLoanSchedule(loanId);
  const { data: transactions, isLoading: transactionsLoading } = useLoanTransactions(loanId);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getLoanStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'PAID':
        return 'info';
      case 'OVERDUE':
        return 'warning';
      case 'DEFAULTED':
      case 'WRITTEN_OFF':
        return 'error';
      default:
        return 'default';
    }
  };

  const getScheduleStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'success';
      case 'PENDING':
        return 'default';
      case 'OVERDUE':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleRecordPayment = () => {
    navigate(`/payments?loanId=${loanId}`);
  };

  if (loanError) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/loans/active')}
          sx={{ mb: 2 }}
        >
          Back to Loans
        </Button>
        <Alert severity="error">
          Failed to load loan details. {loanError instanceof Error ? loanError.message : 'Please try again.'}
        </Alert>
      </Box>
    );
  }

  if (loanLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!loan) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/loans/active')}
          sx={{ mb: 2 }}
        >
          Back to Loans
        </Button>
        <Alert severity="warning">Loan not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/loans/active')}
            sx={{ mb: 2 }}
          >
            Back to Loans
          </Button>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Loan {loan.loanNumber}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip
              label={loan.loanStatus}
              color={getLoanStatusColor(loan.loanStatus) as 'success' | 'warning' | 'error' | 'info' | 'default'}
            />
            {loan.daysOverdue > 0 && (
              <Chip
                label={`${loan.daysOverdue} days overdue`}
                color="error"
                size="small"
              />
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PaymentIcon />}
            onClick={handleRecordPayment}
            disabled={loan.loanStatus === 'PAID'}
          >
            Record Payment
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<MpesaIcon />}
            onClick={() => setMpesaDialogOpen(true)}
            disabled={loan.loanStatus === 'PAID'}
          >
            Pay via M-Pesa
          </Button>
        </Stack>
      </Box>

      <Stack spacing={3}>
        {/* Customer Information and Loan Summary */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Customer Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {loan.customerName || 'N/A'}
                  </Typography>
                </Box>
                {loan.customerPhone && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Phone Number
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PhoneIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body1" fontWeight="medium">
                        {loan.customerPhone}
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Customer ID
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {loan.customerId}
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => navigate(`/customers/${loan.customerId}`)}
              >
                View Customer Profile
              </Button>
            </CardContent>
          </Card>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Loan Summary
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Principal Amount
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {formatCurrency(loan.principalAmount)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Interest Amount
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {formatCurrency(loan.interestAmount)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Processing Fee
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {formatCurrency(loan.processingFee)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Amount Due
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {formatCurrency(loan.totalAmountDue)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Disbursed Amount
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  {formatCurrency(loan.disbursedAmount)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Amount Paid
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="info.main">
                  {formatCurrency(loan.amountPaid)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Outstanding
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="error.main">
                  {formatCurrency(loan.amountOutstanding)}
                </Typography>
              </Box>
              {loan.penaltyAmount > 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Penalty Amount
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="error.main">
                    {formatCurrency(loan.penaltyAmount)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Loan Details */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Loan Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Loan ID
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {loan.loanId}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Loan Number
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {loan.loanNumber}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Tenor
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {loan.tenorWeeks} {loan.tenorWeeks === 1 ? 'week' : 'weeks'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Product ID
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {loan.productId}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Disbursement Date
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {format(new Date(loan.disbursementDate), 'MMM dd, yyyy')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Due Date
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {format(new Date(loan.dueDate), 'MMM dd, yyyy')}
              </Typography>
            </Box>
            {loan.disbursementMethod && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Disbursement Method
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {loan.disbursementMethod}
                </Typography>
              </Box>
            )}
            {loan.disbursementReference && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Disbursement Reference
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {loan.disbursementReference}
                </Typography>
              </Box>
            )}
            {loan.createdAt && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Created At
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {format(new Date(loan.createdAt), 'MMM dd, yyyy HH:mm')}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Payment Schedule */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Payment Schedule
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Due Date</strong></TableCell>
                  <TableCell align="right"><strong>Principal Due</strong></TableCell>
                  <TableCell align="right"><strong>Interest Due</strong></TableCell>
                  <TableCell align="right"><strong>Total Due</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scheduleLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : schedule && schedule.length > 0 ? (
                  schedule.map((item, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        {format(new Date(item.dueDate), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.principalDue)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.interestDue)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="medium">
                          {formatCurrency(item.totalDue)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.status}
                          size="small"
                          color={getScheduleStatusColor(item.status) as 'success' | 'warning' | 'error' | 'info' | 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No payment schedule available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Transaction History */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Transaction History
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Payment Date</strong></TableCell>
                  <TableCell><strong>Payment Reference</strong></TableCell>
                  <TableCell><strong>Payment Method</strong></TableCell>
                  <TableCell align="right"><strong>Amount</strong></TableCell>
                  <TableCell align="right"><strong>Principal Paid</strong></TableCell>
                  <TableCell align="right"><strong>Interest Paid</strong></TableCell>
                  <TableCell align="right"><strong>Penalty Paid</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactionsLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : transactions && transactions.length > 0 ? (
                  transactions.map((transaction) => (
                    <TableRow key={transaction.paymentId} hover>
                      <TableCell>
                        {format(new Date(transaction.paymentDate), 'MMM dd, yyyy HH:mm')}
                      </TableCell>
                      <TableCell>{transaction.paymentReference}</TableCell>
                      <TableCell>{transaction.paymentMethod}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="medium">
                          {formatCurrency(transaction.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(transaction.principalPaid)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(transaction.interestPaid)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(transaction.penaltyPaid)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.paymentStatus}
                          size="small"
                          color={
                            transaction.paymentStatus === 'VERIFIED'
                              ? 'success'
                              : transaction.paymentStatus === 'PENDING'
                              ? 'warning'
                              : 'error'
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No transactions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>

      {/* M-Pesa STK Push Dialog */}
      <MpesaPaymentDialog
        open={mpesaDialogOpen}
        onClose={() => setMpesaDialogOpen(false)}
        loan={loan}
      />
    </Box>
  );
};

export default LoanDetails;
